import { ErrorMessage, Field, useFormikContext } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  InputGroup,
  FormControl,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  Button,
  Badge,
  Spinner,
  Card,
} from "react-bootstrap";
import {
  FaPlusCircle,
  FaSearch,
  FaTrash,
  FaUserCircle,
  FaBuilding,
  FaPhoneAlt,
  FaWallet,
  FaEdit,
  FaPlusSquare,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import apiService from "../../services/apiServices";

import { fetchCustomers, clearSearchResults } from "../../redux/features/customer.features";
import CustomerForm from "../customers/CustomerForm";
import { formatCurrency, PlayError, PlaySuccess } from "../../utility";

/* =========================================================
   PURE DOMAIN HELPERS
========================================================= */
/* =========================================================
   PURE DOMAIN HELPERS & CONSTANTS
========================================================= */
const UI_CONSTANTS = {
  avatarSize: 32,
  actionButtonSize: 26,
  fontSize: {
    xs: '0.6rem',
    sm: '0.68rem',
    md: '0.75rem',
    lg: '0.85rem',
    xl: '0.95rem',
  }
};

const getBalanceState = (balance, limit) => {
  if (balance > 0) return { status: 'credit', color: 'success', label: 'Credit Balance' };
  if (balance === 0) return { status: 'zero', color: 'secondary', label: 'Zero Balance' };

  const absDue = Math.abs(balance);
  if (limit > 0 && absDue >= limit) return { status: 'exceeded', color: 'danger', label: 'Limit Exceeded' };

  return { status: 'due', color: 'warning', label: 'Amount Due' };
};

const getBalanceInfo = (balance = 0, creditLimit) => {
  const absBalance = Math.abs(balance);
  const state = getBalanceState(balance, creditLimit);

  const creditLabel =
    creditLimit === null || creditLimit === undefined
      ? "Unlimited Credit"
      : creditLimit === 0
        ? "No Credit"
        : `Limit: ${formatCurrency(creditLimit)}`;

  return { ...state, absBalance, creditLabel };
};
/* =========================================================
   COMPONENT SECTIONS (Memoized)
========================================================= */
const AvatarSection = React.memo(({ isBusiness, firstName }) => (
  <div
    className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
    style={{
      width: UI_CONSTANTS.avatarSize,
      height: UI_CONSTANTS.avatarSize,
      background: isBusiness ? '#4f46e5' : '#14b8a6',
      fontSize: UI_CONSTANTS.fontSize.lg,
    }}
  >
    {isBusiness ? <FaBuilding /> : (firstName?.charAt(0).toUpperCase() || <FaUserCircle />)}
  </div>
));

const IdentitySection = React.memo(({ customer }) => {
  const displayName = customer.customer_type === "Business"
    ? ((customer.first_name + " " + customer.last_name).trim() || customer.company_name || "Business Account")
    : (customer.first_name + " " + customer.last_name).trim();

  return (
    <div style={{ wordBreak: 'break-word' }}>
      <div className="fw-bold text-dark m-0 text-truncate" style={{ fontSize: UI_CONSTANTS.fontSize.lg, lineHeight: '1.2', maxWidth: '140px' }} title={displayName}>
        {displayName}
      </div>
      <div className="d-flex align-items-center gap-1 mt-0">
        <Badge bg="light" text="dark" className="border border-secondary border-opacity-25 px-1 py-0" style={{ fontSize: UI_CONSTANTS.fontSize.xs }}>
          {customer.customer_id}
        </Badge>
        <small className="text-secondary fw-semibold text-nowrap" style={{ fontSize: UI_CONSTANTS.fontSize.xs }}>
          {customer.phone_number || "N/A"}
        </small>
      </div>
    </div>
  );
});

const FinancialSummary = React.memo(({ balanceInfo }) => {
  if (!balanceInfo) return null;
  return (
    <div className="d-flex flex-column align-items-end">
      <div className="d-flex align-items-center gap-1">
        <div className={`rounded-circle bg-${balanceInfo.color}`} style={{ width: '6px', height: '6px', flexShrink: 0 }}></div>
        <span className={`fw-bold text-${balanceInfo.color} text-nowrap`} style={{ fontSize: UI_CONSTANTS.fontSize.md }}>
          {formatCurrency(balanceInfo.absBalance)}
        </span>
      </div>
      <small className="text-secondary text-nowrap" style={{ fontSize: UI_CONSTANTS.fontSize.xs, textTransform: 'uppercase' }}>
        {balanceInfo.label}
      </small>
    </div>
  );
});

const ActionButtons = React.memo(({ onEdit, onClear }) => (
  <div className="d-flex align-items-center gap-1">
    <Button
      variant="light"
      aria-label="Edit Customer"
      className="d-flex align-items-center justify-content-center text-primary rounded-circle flex-shrink-0"
      style={{ width: UI_CONSTANTS.actionButtonSize, height: UI_CONSTANTS.actionButtonSize, padding: 0, border: 'none', background: 'rgba(13, 110, 253, 0.1)' }}
      onClick={onEdit}
      title="Edit Customer"
    >
      <FaEdit size={11} />
    </Button>
    <Button
      variant="light"
      aria-label="Remove Customer"
      className="d-flex align-items-center justify-content-center text-danger rounded-circle flex-shrink-0"
      style={{ width: UI_CONSTANTS.actionButtonSize, height: UI_CONSTANTS.actionButtonSize, padding: 0, border: 'none', background: 'rgba(220, 53, 69, 0.1)' }}
      onClick={onClear}
      title="Remove Customer"
    >
      <FaTimes size={11} />
    </Button>
  </div>
));

const SelectedCustomerCard = React.memo(({ selectedCustomer, onEdit, onClear }) => {
  const balanceInfo = useMemo(() => {
    return selectedCustomer ? getBalanceInfo(
      selectedCustomer?.account?.balance,
      selectedCustomer?.creditLimit
    ) : null;
  }, [selectedCustomer]);

  if (!selectedCustomer) return null;

  return (
    <Card className="border shadow-2xs bg-white" style={{ borderRadius: '8px', overflow: 'hidden' }}>
      <Card.Body className="p-2 d-flex align-items-center justify-content-between gap-2">
        <div className="d-flex align-items-center gap-2">
          <AvatarSection
            isBusiness={selectedCustomer.customer_type === "Business"}
            firstName={selectedCustomer.first_name}
          />
          <IdentitySection customer={selectedCustomer} />
        </div>

        <div className="d-flex align-items-center gap-2">
          <FinancialSummary balanceInfo={balanceInfo} />
          <ActionButtons onEdit={onEdit} onClear={onClear} />
        </div>
      </Card.Body>
    </Card>
  );
});

/* =========================================================
   COMPONENT
========================================================= */
const CustomerSearchField = ({ name = "customer", label, onSelect, value, onChange, placeholder }) => {
  const dispatch = useDispatch();
  let formikContext = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    formikContext = useFormikContext();
  } catch (e) {
    formikContext = null;
  }
  const values = formikContext?.values || {};
  const setFieldValue = formikContext?.setFieldValue;

  const { customers, searchResults } = useSelector(
    (state) => state.customersReducer
  );

  /* -----------------------------
     LOCAL UI STATE (search only)
  ----------------------------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [isUpdatingCredit, setIsUpdatingCredit] = useState(false);

  const activeSearchRef = useRef("");
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  // Click outside to hide dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* -----------------------------
     DERIVED SELECTED CUSTOMER
  ----------------------------- */
  const rawVal = value !== undefined ? value : (name ? values[name] : null);
  const selectedCustomerId = typeof rawVal === "object" && rawVal !== null ? (rawVal._id || rawVal.value) : rawVal;

  const selectedCustomer = useMemo(() => {
    if (!selectedCustomerId) return null;
    if (typeof value === "object" && value !== null && value._id) return value;
    if (values.selectedCustomer && values.selectedCustomer._id === selectedCustomerId) {
      return values.selectedCustomer;
    }
    return (
      (searchResults || []).find((c) => c._id === selectedCustomerId) ||
      (customers || []).find((c) => c._id === selectedCustomerId) ||
      null
    );
  }, [customers, searchResults, selectedCustomerId, values.selectedCustomer, value]);


  /* -----------------------------
     SEARCH EFFECT
     - debounced
     - race-condition safe
  ----------------------------- */
  useEffect(() => {
    const term = searchTerm.trim();

    if (term === "") {
      // Clear stale dropdown results so old searches don't linger between POS sessions
      dispatch(clearSearchResults());
      return;
    }

    activeSearchRef.current = term;
    setIsSearching(true);
    setShowDropdown(true); // Show dropdown when searching

    const timer = setTimeout(() => {
      dispatch(fetchCustomers({ search: term, page: 1, limit: 50, isDropdownSearch: true }))
        .unwrap?.()
        .finally(() => {
          if (activeSearchRef.current === term) setIsSearching(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, dispatch]);

  /* -----------------------------
     ACTIONS
  ----------------------------- */
  const selectCustomer = (customer) => {
    if (!customer) return;

    if (selectedCustomerId !== customer._id) {
      if (setFieldValue && name) {
        setFieldValue(name, customer._id);
        setFieldValue("selectedCustomer", customer);
      }
      onChange?.(customer._id || customer);
      onSelect?.(customer);
      PlaySuccess();
    }
    setSearchTerm("");
    setShowDropdown(false); // Hide on select
  };

  const clearCustomer = () => {
    if (setFieldValue && name) {
      setFieldValue(name, "");
      setFieldValue("selectedCustomer", null);
    }
    onChange?.(null);
    onSelect?.(null);
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleClickInput = () => {
    setShowDropdown(true);
    if (searchTerm === "") {
      // Must use isDropdownSearch:true — results go into state.searchResults (what the dropdown reads).
      // Without this flag, data goes into state.customers which the dropdown does NOT render.
      dispatch(fetchCustomers({ page: 1, limit: 20, isDropdownSearch: true }));
    }
  };

  const handleUpdateLimit = async (delta) => {
    if (!selectedCustomer) return;

    const newLimit = Math.max(0, (selectedCustomer.creditLimit || 0) + delta);
    setIsUpdatingCredit(true);

    try {
      const response = await apiService.patch(`/customers/${selectedCustomer._id}`, { creditLimit: newLimit });
      const updated = response.data?.customer || response.data;

      toast.success(`Credit limit updated to ${formatCurrency(newLimit)}`);

      // Sync Redux
      await dispatch(fetchCustomers({ search: selectedCustomer._id, limit: 1, isDropdownSearch: true }));

      // Update Formik
      if (updated) {
        setFieldValue("selectedCustomer", updated);
      }

    } catch (error) {
      toast.error("Failed to update credit limit");
      console.error(error);
    } finally {
      setIsUpdatingCredit(false);
    }
  };

  /* -----------------------------
     UI SUBCOMPONENTS
  ----------------------------- */
  const AccountBalance = ({ balance, creditLimit }) => {
    const info = getBalanceInfo(balance, creditLimit);

    return (
      <div className="d-flex flex-column align-items-end">
        <Badge bg={info.isDue ? "danger" : "success"}>
          {info.isDue ? "Due" : "Balance"}: {formatCurrency(info.absBalance)}
        </Badge>
        <small className="text-muted">{info.creditLabel}</small>
      </div>
    );
  };

  const SelectedCustomerCard_legacy = () => (
    <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '12px', overflow: 'hidden' }}>
      <Card.Body className="p-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex flex-1 justify-content-between align-items-center gap-3">
          <div
            className="rounded-circle text-white d-flex align-items-center justify-content-center shadow-sm fw-bold"
            style={{
              width: 42,
              height: 42,
              background: selectedCustomer.customer_type === "Business" ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              fontSize: '1.2rem',
              flexShrink: 0
            }}
          >
            {selectedCustomer.first_name?.charAt(0).toUpperCase() || (selectedCustomer.customer_type === "Business" ? <FaBuilding /> : <FaUserCircle />)}
          </div>

          <div style={{ wordBreak: 'break-word' }}>
            <div className="fw-bolder text-dark m-0" style={{ fontSize: '1.05rem', lineHeight: '1.2' }}>
              {selectedCustomer.first_name} {selectedCustomer.last_name}
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
              <Badge bg="light" text="dark" className="border border-secondary border-opacity-25 fw-bolder" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                {selectedCustomer.customer_id}
              </Badge>
              <small className="text-secondary fw-bolder text-nowrap" style={{ fontSize: '0.75rem' }}>
                <FaPhoneAlt size={10} className="me-1 opacity-75" />
                {selectedCustomer.phone_number || "N/A"}
              </small>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center flex-wrap justify-content-between gap-3 flex-grow-1 flex-md-grow-0 mt-2 mt-sm-0">
          <div className="text-end d-flex flex-column align-items-start align-items-sm-end flex-grow-1 flex-sm-grow-0">
            {(() => {
              const info = getBalanceInfo(selectedCustomer?.account?.balance, selectedCustomer?.creditLimit);
              return (
                <>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <div className={`rounded-circle ${info.isDue ? 'bg-danger' : 'bg-success'}`} style={{ width: '8px', height: '8px', flexShrink: 0 }}></div>
                    <span className={`fw-bolder ${info.isDue ? 'text-danger' : 'text-success'} text-nowrap`} style={{ fontSize: '1.05rem' }}>
                      {formatCurrency(info.absBalance)}
                    </span>
                  </div>
                  <small className="text-secondary fw-bolder text-nowrap" style={{ fontSize: '0.65rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    {info.creditLabel}
                  </small>
                </>
              );
            })()}
          </div>

          <div className="d-flex align-items-center gap-2 border-start border-2 border-light ps-3">
            {!(
              selectedCustomer.isWalkIn === true ||
              selectedCustomer.isSystemProtected === true ||
              selectedCustomer.customer_id === "CUS-000001" ||
              selectedCustomer.customer_id === "CUS-WALKIN" ||
              selectedCustomer.email === "walkin@store.local" ||
              selectedCustomer.phone_number === "0000000000"
            ) && (
              <Button
                variant="light"
                className="d-flex align-items-center justify-content-center text-primary bg-opacity-10 rounded-circle flex-shrink-0"
                style={{ width: '34px', height: '34px', padding: 0, border: 'none', background: 'rgba(13, 110, 253, 0.1)' }}
                onClick={() => setShowEditCustomerModal(true)}
                title="Edit Customer"
              >
                <FaEdit size={14} />
              </Button>
            )}
            <Button
              variant="light"
              className="d-flex align-items-center justify-content-center text-danger rounded-circle flex-shrink-0"
              style={{ width: '34px', height: '34px', padding: 0, border: 'none', background: 'rgba(220, 53, 69, 0.1)' }}
              onClick={clearCustomer}
              title="Remove Customer"
            >
              <FaTimes size={14} />
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="position-relative w-100" ref={containerRef}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="text-secondary fw-bolder text-uppercase mb-0" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
          <FaUserCircle className="me-1 opacity-75" size={12} /> {label}
        </label>
        {!selectedCustomer && (
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              className="py-1 px-3 fw-bold text-uppercase shadow-sm hover-elevate transition-all"
              style={{ fontSize: '0.62rem', borderRadius: '20px', letterSpacing: '0.3px', borderWidth: '1.5px' }}
              onClick={async () => {
                setIsSearching(true);
                try {
                  const res = await dispatch(fetchCustomers({ search: "CUS-000001", limit: 5, isDropdownSearch: true })).unwrap();
                  const customerList = Array.isArray(res) ? res : (res.data || []);
                  let walkIn = customerList.find(c => 
                    c.customer_id === "CUS-000001" || 
                    c.customer_id === "CUS-WALKIN" || 
                    c.isWalkIn === true ||
                    c.email === "walkin@store.local" ||
                    (c.first_name && c.first_name.toLowerCase().includes("walk-in"))
                  );
                  if (walkIn) selectCustomer(walkIn);
                  else toast.warning("System default Walk-In profile not found.");
                } finally {
                  setIsSearching(false);
                }
              }}
            >
              Quick Walk-in
            </Button>
            {searchTerm && (
              <Button
                variant="outline-secondary"
                className="py-1 px-3 fw-bold text-uppercase shadow-sm hover-elevate transition-all"
                style={{ fontSize: '0.62rem', borderRadius: '20px', letterSpacing: '0.3px', borderWidth: '1.5px' }}
                onClick={() => setSearchTerm("")}
              >
                Reset
              </Button>
            )}
          </div>
        )}
      </div>

      {selectedCustomer ? (
        <SelectedCustomerCard
          selectedCustomer={selectedCustomer}
          onEdit={() => setShowEditCustomerModal(true)}
          onClear={clearCustomer}
        />
      ) : (
        <>
          <div className="d-flex align-items-center w-100">
            <div className="flex-grow-1 position-relative">
              <FormControl
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleClickInput}
                placeholder="SEARCH CUSTOMER..."
                className="shadow-sm border"
                style={{
                  height: '38px',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                }}
              />
              {isSearching && (
                <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
                  <Spinner size="sm" animation="border" variant="primary" />
                </div>
              )}
            </div>
            <Button
              variant="outline-primary"
              onClick={() => setShowCustomerModal(true)}
              className="ms-2 d-flex align-items-center justify-content-center shadow-sm"
              style={{ height: '38px', width: '42px', padding: 0 }}
              title="Add Customer"
            >
              <FaPlusSquare size={18} />
            </Button>
          </div>

          {(searchResults || []).length > 0 && showDropdown && (
            <div
              className="shadow border bg-white position-absolute w-100"
              style={{ zIndex: 9999, maxHeight: '300px', overflowY: 'auto' }}
            >
              <ListGroup variant="flush">
                {(searchResults || []).map((c) => (
                  <ListGroupItem
                    key={c._id}
                    action
                    onClick={() => selectCustomer(c)}
                    className="d-flex justify-content-between"
                  >
                    <div>
                      <div className="fw-bold">
                        {c.first_name} {c.last_name}
                      </div>
                      <small className="text-muted">
                        {c.customer_id} | {c.phone_number}
                      </small>
                    </div>

                    <AccountBalance
                      balance={c?.account?.balance}
                      creditLimit={c?.creditLimit}
                    />
                  </ListGroupItem>
                ))}
              </ListGroup>
            </div>
          )}
        </>
      )}

      <Modal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        size="lg"
        centered
        contentClassName="border-0 shadow-lg overflow-hidden rounded-3"
      >
        <ModalBody className="p-0 border-0">
          <CustomerForm
            isPopUp
            setShow={(v) => {
              if (!v) {
                setShowCustomerModal(false);
                dispatch(fetchCustomers());
              }
            }}
          />
        </ModalBody>
      </Modal>

      <Modal
        show={showEditCustomerModal}
        onHide={() => setShowEditCustomerModal(false)}
        size="lg"
        centered
        contentClassName="border-0 shadow-lg overflow-hidden rounded-3"
      >
        <ModalBody className="p-0 border-0">
          <CustomerForm
            isPopUp
            isEditMode={true}
            selectedCustomer={selectedCustomer}
            setShow={(v) => {
              if (!v) {
                setShowEditCustomerModal(false);
              }
            }}
            savedCompleted={async () => {
              if (selectedCustomer?._id) {
                await dispatch(fetchCustomers({ search: selectedCustomer._id, limit: 1, isDropdownSearch: true }));
                const res = await apiService.get(`/customers/${selectedCustomer._id}`);
                if (res.data?.customer) {
                  setFieldValue("selectedCustomer", res.data.customer);
                }
              }
            }}
          />
        </ModalBody>
      </Modal>

      <Modal
        show={showCreditModal}
        onHide={() => setShowCreditModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white py-2">
          <Modal.Title className="h6 mb-0">Adjust Credit Limit</Modal.Title>
        </Modal.Header>
        <ModalBody className="p-4">
          <div className="text-center mb-4">
            <h4 className="fw-bold mb-1">{formatCurrency(selectedCustomer?.creditLimit || 0)}</h4>
            <small className="text-muted text-uppercase">Current Limit</small>
          </div>

          <div className="d-grid gap-3">
            <div className="row g-2">
              <div className="col-6">
                <Button
                  variant="outline-danger"
                  className="w-100 py-2"
                  onClick={() => handleUpdateLimit(-500)}
                  disabled={isUpdatingCredit}
                >
                  -500
                </Button>
              </div>
              <div className="col-6">
                <Button
                  variant="outline-success"
                  className="w-100 py-2"
                  onClick={() => handleUpdateLimit(500)}
                  disabled={isUpdatingCredit}
                >
                  +500
                </Button>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-6">
                <Button
                  variant="outline-danger"
                  className="w-100 py-2"
                  onClick={() => handleUpdateLimit(-1000)}
                  disabled={isUpdatingCredit}
                >
                  -1000
                </Button>
              </div>
              <div className="col-6">
                <Button
                  variant="outline-success"
                  className="w-100 py-2"
                  onClick={() => handleUpdateLimit(1000)}
                  disabled={isUpdatingCredit}
                >
                  +1000
                </Button>
              </div>
            </div>

            <hr className="my-1" />

            <Button
              variant="primary"
              className="py-2 fw-bold"
              onClick={() => setShowCreditModal(false)}
              disabled={isUpdatingCredit}
            >
              DONE
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {formikContext && (
        <>
          <Field type="hidden" name={name} />
          <ErrorMessage name={name}>
            {msg => typeof msg === 'string' ? <div className="text-danger small mt-1">{msg}</div> : null}
          </ErrorMessage>
        </>
      )}
    </div>
  );
};

export default CustomerSearchField;
